import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProvinceContext = createContext(null);

const STORAGE_KEY = 'selected_province';
const ALL_PROVINCES_KEY = 'all';
const PREV_PROVINCE_KEY = 'prev_province_before_listings';

// Listing bölümleri (bu bölümlere girildiğinde All İller selectedr)
const LISTING_SECTIONS = ['/property', '/vehicles', '/araclar', '/jobs', '/events', '/market', '/search', '/listing/', '/pazaryeri'];

export function ProvinceProvider({ children }) {
  const [provinces, setProvinces] = useState([]);
  const [selectedProvince, setSelectedProvinceState] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [loading, setLoading] = useState(true);
  const geoAttempted = useRef(false);

  useEffect(() => {
    const fetchProvinces = async () => {
      try {
        const res = await axios.get(`${API}/provinces/active`);
        const provs = res.data?.provinces || [];
        setProvinces(provs);

        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved === ALL_PROVINCES_KEY) {
          setSelectedProvinceState(null);
        } else if (saved) {
          const found = provs.find(p => p.region_id === saved);
          if (found) {
            setSelectedProvinceState(found);
          } else {
            setSelectedProvinceState(null);
            localStorage.setItem(STORAGE_KEY, ALL_PROVINCES_KEY);
          }
        } else {
          // First visit: priority order =
          //   1. Mobile geolocation (if user grants permission)
          //   2. Logged-in user's saved profile province (county)
          //   3. Fall back to "All UK"   ← current default, unchanged
          let resolved = false;
          if (!geoAttempted.current && navigator.geolocation) {
            geoAttempted.current = true;
            try {
              const pos = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
              });
              const { latitude, longitude } = pos.coords;
              const geoRes = await axios.get(`${API}/geocode/reverse?lat=${latitude}&lng=${longitude}`).catch(() => null);
              if (geoRes?.data?.province_name) {
                const matchedProv = provs.find(p =>
                  p.name.toLowerCase() === geoRes.data.province_name.toLowerCase()
                );
                if (matchedProv) {
                  setSelectedProvinceState(matchedProv);
                  localStorage.setItem(STORAGE_KEY, matchedProv.region_id);
                  resolved = true;
                }
              }
            } catch (e) { /* geolocation denied or failed */ }
          }
          // iter485: profile fallback when mobile geolocation didn't resolve
          if (!resolved) {
            try {
              const meRes = await axios.get(`${API}/auth/me`).catch(() => null);
              const meProvId = meRes?.data?.province_id;
              if (meProvId) {
                const matchedProv = provs.find(p => p.region_id === meProvId);
                if (matchedProv) {
                  setSelectedProvinceState(matchedProv);
                  localStorage.setItem(STORAGE_KEY, matchedProv.region_id);
                  resolved = true;
                }
              }
            } catch (e) { /* anonymous visitor — fine */ }
          }
          if (!resolved) {
            setSelectedProvinceState(null);
            localStorage.setItem(STORAGE_KEY, ALL_PROVINCES_KEY);
          }
        }
      } catch (err) {
        // Non-critical: silently handled
      } finally {
        setLoading(false);
      }
    };
    fetchProvinces();
  }, []);

  useEffect(() => {
    if (!selectedProvince) {
      setDistricts([]);
      return;
    }
    const fetchDistricts = async () => {
      try {
        const res = await axios.get(`${API}/provinces/${selectedProvince.region_id}/districts`);
        setDistricts(res.data?.districts || []);
      } catch (err) {
        // Non-critical: silently handled
      }
    };
    fetchDistricts();
  }, [selectedProvince]);

  const setSelectedProvince = useCallback((province) => {
    setSelectedProvinceState(province);
    if (province) {
      localStorage.setItem(STORAGE_KEY, province.region_id);
    } else {
      localStorage.setItem(STORAGE_KEY, ALL_PROVINCES_KEY);
    }
  }, []);

  // Listing bölümüne girildiğinde All İller selectedr, çıkıldığında agoki il geri gelir
  const enterListingSection = useCallback(() => {
    const current = localStorage.getItem(STORAGE_KEY);
    if (current !== ALL_PROVINCES_KEY) {
      localStorage.setItem(PREV_PROVINCE_KEY, current || ALL_PROVINCES_KEY);
    }
    setSelectedProvinceState(null);
    localStorage.setItem(STORAGE_KEY, ALL_PROVINCES_KEY);
  }, []);

  const exitListingSection = useCallback(() => {
    const prev = localStorage.getItem(PREV_PROVINCE_KEY);
    if (prev && prev !== ALL_PROVINCES_KEY) {
      const found = provinces.find(p => p.region_id === prev);
      if (found) {
        setSelectedProvinceState(found);
        localStorage.setItem(STORAGE_KEY, prev);
      }
    }
    localStorage.removeItem(PREV_PROVINCE_KEY);
  }, [provinces]);

  const isAllProvinces = selectedProvince === null;

  return (
    <ProvinceContext.Provider value={{
      provinces,
      selectedProvince,
      setSelectedProvince,
      districts,
      loading,
      isAllProvinces,
      enterListingSection,
      exitListingSection,
      LISTING_SECTIONS
    }}>
      {children}
    </ProvinceContext.Provider>
  );
}

export function useProvince() {
  const ctx = useContext(ProvinceContext);
  if (!ctx) throw new Error('useProvince must be used within ProvinceProvider');
  return ctx;
}
